'use client';

import React from 'react';

/**
 * AURA ABSTRACT ICONS (AAI)
 * Sistema de iconografía geométrica vectorial 100% original, propietaria y legal.
 * Estilo: Planos 2.5D superpuestos, gradientes saturados de alta gama, sombras suaves y geometrías abstractas.
 */

const BaseSquircle = ({ id = "bg" }) => (
  <>
    <defs>
      <linearGradient id={`base_sq_${id}`} x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#ffffff" />
        <stop offset="100%" stopColor="#f8fafc" />
      </linearGradient>
      <filter id={`sq_shadow_${id}`} x1="-10%" y1="-10%" width="125%" height="125%">
        <feDropShadow dx="0" dy="2" stdDeviation="2" floodColor="#0f172a" floodOpacity="0.05" />
      </filter>
    </defs>
    <rect
      x="2"
      y="2"
      width="60"
      height="60"
      rx="15"
      fill={`url(#base_sq_${id})`}
      stroke="#e2e8f0"
      strokeWidth="1.2"
      filter={`url(#sq_shadow_${id})`}
    />
  </>
);

// 1. DASHBOARD PRINCIPAL (Pilares geométricos ascendentes en degradado)
export function AuraDashboardIcon({ size = 64, className = "" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
      <BaseSquircle id="dash" />
      <defs>
        <linearGradient id="pilar_blue" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#38bdf8" />
          <stop offset="100%" stopColor="#0284c7" />
        </linearGradient>
        <linearGradient id="pilar_purple" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#c084fc" />
          <stop offset="100%" stopColor="#7e22ce" />
        </linearGradient>
        <linearGradient id="pilar_amber" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#fbbf24" />
          <stop offset="100%" stopColor="#d97706" />
        </linearGradient>
      </defs>
      {/* Pilar 1 (Medio) */}
      <rect x="15" y="29" width="9" height="21" rx="4.5" fill="url(#pilar_blue)" />
      {/* Pilar 2 (Alto) */}
      <rect x="27.5" y="16" width="9" height="34" rx="4.5" fill="url(#pilar_purple)" />
      {/* Pilar 3 (Corto/Frontal) */}
      <rect x="40" y="24" width="9" height="26" rx="4.5" fill="url(#pilar_amber)" />
      {/* Halo de crecimiento */}
      <circle cx="32" cy="12" r="2.5" fill="#a855f7" opacity="0.8" />
    </svg>
  );
}

// 2. CONTACTOS (Silueta geométrica minimalista con arco envolvente en esmeralda)
export function AuraContactsIcon({ size = 64, className = "" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
      <BaseSquircle id="cont" />
      <defs>
        <linearGradient id="cont_emerald" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#10b981" />
          <stop offset="100%" stopColor="#047857" />
        </linearGradient>
        <linearGradient id="cont_teal" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#2dd4bf" />
          <stop offset="100%" stopColor="#0f766e" />
        </linearGradient>
      </defs>
      {/* Núcleo de avatar abstracto */}
      <circle cx="32" cy="24" r="7.5" fill="url(#cont_emerald)" />
      {/* Arco envolvente abstracto (cuerpo geométrico) */}
      <path
        d="M17 46C17 38 23.5 35 32 35C40.5 35 47 38 47 46"
        stroke="url(#cont_teal)"
        strokeWidth="6"
        strokeLinecap="round"
      />
      {/* Indicador de relación activa */}
      <circle cx="44" cy="20" r="3" fill="#34d399" />
    </svg>
  );
}

// 3. INMUEBLES / PROPIEDADES INDIVIDUALES (Prisma arquitectónico abstracto en 2.5D)
export function AuraPropertiesIcon({ size = 64, className = "" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
      <BaseSquircle id="prop" />
      <defs>
        <linearGradient id="prop_roof" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#f43f5e" />
          <stop offset="100%" stopColor="#be123c" />
        </linearGradient>
        <linearGradient id="prop_wall_left" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#06b6d4" />
          <stop offset="100%" stopColor="#0891b2" />
        </linearGradient>
        <linearGradient id="prop_wall_right" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#22d3ee" />
          <stop offset="100%" stopColor="#0284c7" />
        </linearGradient>
      </defs>
      {/* Plano angular superior (Techo geométrico) */}
      <path d="M32 15L16 26L32 23L48 26L32 15Z" fill="url(#prop_roof)" />
      {/* Fachada isométrica izquierda */}
      <path d="M16 26L32 23V47L16 43V26Z" fill="url(#prop_wall_left)" />
      {/* Fachada isométrica derecha */}
      <path d="M32 23L48 26V43L32 47V23Z" fill="url(#prop_wall_right)" />
      {/* Ventanal abstracto luminoso */}
      <path d="M36 30L44 32V39L36 37V30Z" fill="#ffffff" opacity="0.85" />
    </svg>
  );
}

// 4. PROYECTOS Y DESARROLLOS (Torres arquitectónicas verticales escalonadas en azul zafiro y violeta)
export function AuraDevelopmentsIcon({ size = 64, className = "" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
      <BaseSquircle id="dev" />
      <defs>
        <linearGradient id="dev_back" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#818cf8" />
          <stop offset="100%" stopColor="#4338ca" />
        </linearGradient>
        <linearGradient id="dev_front" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#38bdf8" />
          <stop offset="100%" stopColor="#0369a1" />
        </linearGradient>
      </defs>
      {/* Torre trasera esbelta con corte en ángulo */}
      <path d="M30 14L46 19V48H30V14Z" fill="url(#dev_back)" rx="3" />
      {/* Torre delantera escalonada */}
      <path d="M17 24L33 21V48H17V24Z" fill="url(#dev_front)" rx="3" />
      {/* Líneas abstractas de pisos */}
      <line x1="21" y1="28" x2="29" y2="28" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" opacity="0.8" />
      <line x1="21" y1="35" x2="29" y2="35" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" opacity="0.8" />
      <line x1="21" y1="42" x2="29" y2="42" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" opacity="0.8" />
      <line x1="36" y1="26" x2="42" y2="26" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" opacity="0.6" />
      <line x1="36" y1="33" x2="42" y2="33" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" opacity="0.6" />
    </svg>
  );
}

// 5. PIPELINE LEADS (Cintas geométricas entrelazadas en forma de embudo y conversión)
export function AuraPipelineIcon({ size = 64, className = "" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
      <BaseSquircle id="pipe" />
      <defs>
        <linearGradient id="pipe_pink" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#fb7185" />
          <stop offset="100%" stopColor="#e11d48" />
        </linearGradient>
        <linearGradient id="pipe_cyan" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#2dd4bf" />
          <stop offset="100%" stopColor="#0d9488" />
        </linearGradient>
      </defs>
      {/* Chevron izquierdo convergente */}
      <path d="M16 20L28 32L16 44" stroke="url(#pipe_pink)" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" />
      {/* Chevron derecho convergente */}
      <path d="M48 20L36 32L48 44" stroke="url(#pipe_cyan)" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" />
      {/* Núcleo central de conversión (Cierre de negocio) */}
      <circle cx="32" cy="32" r="5" fill="#f59e0b" />
    </svg>
  );
}

// 6. CALENDARIO & CITAS (Hoja de calendario geométrica con pliegue moderno en naranja y púrpura)
export function AuraCalendarIcon({ size = 64, className = "" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
      <BaseSquircle id="cal" />
      <defs>
        <linearGradient id="cal_grad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#fb923c" />
          <stop offset="100%" stopColor="#ea580c" />
        </linearGradient>
      </defs>
      {/* Marco de fecha */}
      <rect x="15" y="16" width="34" height="34" rx="7" fill="#f8fafc" stroke="#cbd5e1" strokeWidth="1.5" />
      {/* Cabecera superior curvada */}
      <path d="M15 22C15 18.7 17.7 16 21 16H43C46.3 16 49 18.7 49 22V26H15V22Z" fill="url(#cal_grad)" />
      {/* Pines de encuadernación */}
      <circle cx="23" cy="14" r="2" fill="#475569" />
      <circle cx="41" cy="14" r="2" fill="#475569" />
      {/* Número '31' tipográfico contemporáneo */}
      <text x="32" y="42" fontFamily="system-ui, sans-serif" fontSize="16" fontWeight="900" fill="#0f172a" textAnchor="middle">
        31
      </text>
    </svg>
  );
}

// 7. TURNOS POR PROYECTO (Esfera de tiempo horaria / cuadrante de guardias en turquesa y violeta)
export function AuraShiftsIcon({ size = 64, className = "" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
      <BaseSquircle id="shft" />
      <defs>
        <linearGradient id="shift_ring" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#a855f7" />
          <stop offset="100%" stopColor="#06b6d4" />
        </linearGradient>
      </defs>
      {/* Anillo de rotación de turnos */}
      <circle cx="32" cy="32" r="16" stroke="url(#shift_ring)" strokeWidth="4.5" strokeDasharray="22 5" strokeLinecap="round" />
      {/* Agujas en ángulo de guardia */}
      <path d="M32 22V32L39 36" stroke="#0f172a" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="32" cy="32" r="3.5" fill="#f59e0b" />
    </svg>
  );
}

// 8. GESTIÓN DE USUARIOS / EQUIPO (Trilogía de nodos conectados de alta jerarquía)
export function AuraUsersIcon({ size = 64, className = "" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
      <BaseSquircle id="usr" />
      <defs>
        <linearGradient id="usr_main" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#2563eb" />
          <stop offset="100%" stopColor="#1e40af" />
        </linearGradient>
        <linearGradient id="usr_sec" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#38bdf8" />
          <stop offset="100%" stopColor="#0284c7" />
        </linearGradient>
      </defs>
      {/* Nodo Líder (Central) */}
      <circle cx="32" cy="22" r="6" fill="url(#usr_main)" />
      <path d="M22 43C22 36.5 26.5 33 32 33C37.5 33 42 36.5 42 43" stroke="url(#usr_main)" strokeWidth="4" strokeLinecap="round" />
      {/* Nodo Lateral Izquierdo */}
      <circle cx="19" cy="27" r="4.5" fill="url(#usr_sec)" />
      <path d="M12 45C12 40 15 37.5 19 37.5" stroke="url(#usr_sec)" strokeWidth="3" strokeLinecap="round" />
      {/* Nodo Lateral Derecho */}
      <circle cx="45" cy="27" r="4.5" fill="url(#usr_sec)" />
      <path d="M45 37.5C49 37.5 52 40 52 45" stroke="url(#usr_sec)" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

// 9. AJUSTES GENERALES (Mecanismo concéntrico de precisión en gris titanio y azul cobalto)
export function AuraSettingsIcon({ size = 64, className = "" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
      <BaseSquircle id="set" />
      <defs>
        <linearGradient id="set_titanium" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#64748b" />
          <stop offset="100%" stopColor="#334155" />
        </linearGradient>
      </defs>
      {/* Anillo exterior estilizado */}
      <circle cx="32" cy="32" r="14" stroke="url(#set_titanium)" strokeWidth="4" strokeDasharray="6 3.5" />
      {/* Eje central */}
      <circle cx="32" cy="32" r="7" fill="url(#set_titanium)" />
      <circle cx="32" cy="32" r="3" fill="#ffffff" />
      {/* Satélite de calibración */}
      <circle cx="43" cy="21" r="2.5" fill="#3b82f6" />
    </svg>
  );
}

// 10. CENTRO DE AYUDA (Espiral geométrica de soporte continuo en verde esmeralda y cielo)
export function AuraHelpIcon({ size = 64, className = "" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
      <BaseSquircle id="hlp" />
      <defs>
        <linearGradient id="hlp_grad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#06b6d4" />
          <stop offset="100%" stopColor="#0284c7" />
        </linearGradient>
      </defs>
      {/* Salvavidas / Aro continuo de soporte */}
      <circle cx="32" cy="32" r="14" stroke="url(#hlp_grad)" strokeWidth="6" />
      {/* Cintas cruzadas de asistencia */}
      <line x1="22" y1="22" x2="27" y2="27" stroke="#ffffff" strokeWidth="3" strokeLinecap="round" />
      <line x1="42" y1="42" x2="37" y2="37" stroke="#ffffff" strokeWidth="3" strokeLinecap="round" />
      <line x1="42" y1="22" x2="37" y2="27" stroke="#ffffff" strokeWidth="3" strokeLinecap="round" />
      <line x1="22" y1="42" x2="27" y2="37" stroke="#ffffff" strokeWidth="3" strokeLinecap="round" />
      {/* Centro luminoso */}
      <circle cx="32" cy="32" r="3" fill="#f59e0b" />
    </svg>
  );
}

// 11. SITIO WEB (Esfera geométrica con meridianos de transmisión global en azul cyan)
export function AuraWebsiteIcon({ size = 64, className = "" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
      <BaseSquircle id="web" />
      <defs>
        <linearGradient id="web_globe" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#38bdf8" />
          <stop offset="100%" stopColor="#0284c7" />
        </linearGradient>
      </defs>
      {/* Esfera base */}
      <circle cx="32" cy="32" r="16" fill="url(#web_globe)" />
      {/* Meridianos curvos elípticos */}
      <ellipse cx="32" cy="32" rx="8" ry="16" stroke="#ffffff" strokeWidth="1.8" fill="none" opacity="0.85" />
      <line x1="16" y1="32" x2="48" y2="32" stroke="#ffffff" strokeWidth="1.8" opacity="0.85" />
      <line x1="19" y1="24" x2="45" y2="24" stroke="#ffffff" strokeWidth="1.2" opacity="0.6" />
      <line x1="19" y1="40" x2="45" y2="40" stroke="#ffffff" strokeWidth="1.2" opacity="0.6" />
    </svg>
  );
}

// 12. MARKETING AI & META ADS (Cono de expansión geométrica y ondas dinámicas en naranja y fucsia)
export function AuraMarketingIcon({ size = 64, className = "" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
      <BaseSquircle id="mkt" />
      <defs>
        <linearGradient id="mkt_cone" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#f97316" />
          <stop offset="100%" stopColor="#c2410c" />
        </linearGradient>
        <linearGradient id="mkt_wave" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#f43f5e" />
          <stop offset="100%" stopColor="#a855f7" />
        </linearGradient>
      </defs>
      {/* Cono angular de difusión */}
      <path d="M17 35V29C17 27.5 18 26.5 19.5 26.5H24L38 17V47L24 37.5H19.5C18 37.5 17 36.5 17 35Z" fill="url(#mkt_cone)" />
      {/* Ondas geométricas de audiencia */}
      <path d="M43 23C46.5 26 48.5 28.8 48.5 32C48.5 35.2 46.5 38 43 41" stroke="url(#mkt_wave)" strokeWidth="3.5" strokeLinecap="round" />
      <path d="M49 18C53.5 22 55.5 26.5 55.5 32C55.5 37.5 53.5 42 49 46" stroke="url(#mkt_wave)" strokeWidth="2.8" strokeLinecap="round" opacity="0.7" />
      {/* Destello AI */}
      <circle cx="38" cy="17" r="2.5" fill="#facc15" />
    </svg>
  );
}

// 13. HERRAMIENTAS FINANCIERAS (Calculadora, ROI y Comisiones: compás y prisma en magenta y verde menta)
export function AuraToolsIcon({ size = 64, className = "" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
      <BaseSquircle id="tls" />
      <defs>
        <linearGradient id="tls_magenta" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#ec4899" />
          <stop offset="100%" stopColor="#be185d" />
        </linearGradient>
        <linearGradient id="tls_mint" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#10b981" />
          <stop offset="100%" stopColor="#047857" />
        </linearGradient>
      </defs>
      {/* Barra diagonal del porcentaje / ratio ROI */}
      <path d="M44 17L18 47H24L50 17H44Z" fill="url(#tls_magenta)" />
      {/* Nodo superior de rentabilidad */}
      <circle cx="23" cy="22" r="5.5" fill="url(#tls_mint)" />
      {/* Nodo inferior de comisiones */}
      <circle cx="41" cy="42" r="5.5" fill="url(#tls_mint)" />
      {/* Símbolo de delta / incremento */}
      <polygon points="46,20 52,26 40,26" fill="#f59e0b" />
    </svg>
  );
}

// 14. DOCUMENTOS (Planos geométricos superpuestos en amarillo, cian y carmín)
export function AuraDocumentsIcon({ size = 64, className = "" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
      <BaseSquircle id="doc" />
      {/* Hoja 1 (Atrás - Cian) */}
      <rect x="16" y="16" width="24" height="32" rx="3.5" fill="#38bdf8" transform="rotate(-7 16 16)" />
      {/* Hoja 2 (Centro - Amarillo dorado) */}
      <rect x="20" y="15" width="24" height="32" rx="3.5" fill="#facc15" transform="rotate(5 20 15)" />
      {/* Hoja 3 (Frente - Naranja/Carmín con solapa) */}
      <rect x="22" y="17" width="24" height="31" rx="3.5" fill="#f97316" />
      <path d="M37 17L46 26H37V17Z" fill="#c2410c" />
      {/* Líneas abstractas de texto */}
      <line x1="26" y1="31" x2="41" y2="31" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" opacity="0.9" />
      <line x1="26" y1="37" x2="36" y2="37" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" opacity="0.9" />
    </svg>
  );
}

// 15. EXPEDIENTES & WORKFLOWS (Checkmark geométrico angular en esmeralda y turquesa)
export function AuraWorkflowsIcon({ size = 64, className = "" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
      <BaseSquircle id="wf" />
      <defs>
        <linearGradient id="wf_teal" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#14b8a6" />
          <stop offset="100%" stopColor="#0f766e" />
        </linearGradient>
        <linearGradient id="wf_blue" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#0284c7" />
          <stop offset="100%" stopColor="#0369a1" />
        </linearGradient>
      </defs>
      {/* Brazo corto del check */}
      <path d="M18 34L28 44L34 38L24 28L18 34Z" fill="url(#wf_blue)" />
      {/* Brazo largo del check */}
      <path d="M28 44L47 21L41 16L23 39L28 44Z" fill="url(#wf_teal)" />
      {/* Prisma de validación */}
      <circle cx="47" cy="21" r="3" fill="#34d399" />
    </svg>
  );
}

// 16. REPORTES & CIERRES (Gráfico de dona seccionado en violeta, ámbar y turquesa)
export function AuraReportsIcon({ size = 64, className = "" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
      <BaseSquircle id="rep" />
      <defs>
        <linearGradient id="rep_purple" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#a855f7" />
          <stop offset="100%" stopColor="#7e22ce" />
        </linearGradient>
      </defs>
      {/* Cuadrantes estilo dashboards limpios */}
      <rect x="17" y="17" width="13" height="13" rx="4" fill="url(#rep_purple)" />
      <rect x="34" y="17" width="13" height="13" rx="4" fill="#06b6d4" />
      <rect x="17" y="34" width="13" height="13" rx="4" fill="#f59e0b" />
      <rect x="34" y="34" width="13" height="13" rx="4" fill="#f43f5e" />
    </svg>
  );
}

// 17. WHATSAPP & MENSAJERÍA (Ícono oficial de WhatsApp estilizado Odoo 18 con BaseSquircle)
export function AuraMessagingIcon({ size = 64, className = "" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
      <BaseSquircle id="wa" />
      <defs>
        <linearGradient id="wa_emerald_grad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#25D366" />
          <stop offset="100%" stopColor="#128C7E" />
        </linearGradient>
        <filter id="wa_glow" x="-15%" y="-15%" width="130%" height="130%">
          <feDropShadow dx="0" dy="2.5" stdDeviation="2.5" floodColor="#128C7E" floodOpacity="0.28" />
        </filter>
      </defs>
      
      {/* Contenedor del ícono centrado en 64x64 */}
      <g filter="url(#wa_glow)">
        {/* Globo exterior de WhatsApp con su colita característica (orientada a 7 en punto) */}
        <path
          d="M32 14.5C22.33 14.5 14.5 22.33 14.5 32C14.5 35.25 15.39 38.3 16.94 40.92L15 48.5C14.68 49.68 15.82 50.82 17 50.5L24.58 48.56C26.7 49.8 29.75 50.5 32 50.5C41.67 50.5 49.5 42.67 49.5 32C49.5 22.33 41.67 14.5 32 14.5Z"
          fill="url(#wa_emerald_grad)"
        />
        {/* Reflejo de luz 2.5D superior sutil estilo Odoo */}
        <path
          d="M32 16C23.5 16 16.5 22.5 16.5 30.5C16.5 33 17.2 35.3 18.5 37.3C19.5 30 25.5 24 33 24C38.5 24 43.2 27.2 45.5 31.8C47 28 47.5 24 45.5 20.5C42.5 17.5 37.5 16 32 16Z"
          fill="#ffffff"
          opacity="0.18"
        />
        {/* Auricular telefónico oficial de WhatsApp en blanco puro */}
        <path
          d="M38.8 34.8C38.2 34.5 35.4 33.1 34.9 32.9C34.4 32.7 34 32.6 33.6 33.2C33.2 33.8 32.1 35.2 31.7 35.6C31.3 36 30.9 36.1 30.3 35.8C27.8 34.6 25.8 32.6 24.6 30.1C24.3 29.5 24.4 29.1 24.8 28.7C25.2 28.3 25.7 27.6 26.1 27.1C26.5 26.6 26.6 26.3 26.8 25.8C27 25.3 26.9 24.9 26.7 24.5C26.5 24.1 25.1 20.8 24.5 19.4C24 18 23.4 18.2 23 18.2C22.6 18.2 22.1 18.2 21.7 18.2C21.2 18.2 20.4 18.4 19.8 19C19.1 19.7 17.3 21.4 17.3 24.9C17.3 28.4 19.8 31.7 20.2 32.2C20.6 32.7 25.2 39.8 32.3 42.8C37.4 45 38.5 44.5 39.6 44.4C40.7 44.3 43.1 43 43.6 41.6C44.1 40.2 44.1 39 43.9 38.7C43.7 38.5 43.3 38.4 42.7 38.1L38.8 34.8Z"
          fill="#ffffff"
        />
      </g>
    </svg>
  );
}

// 17.1 EMAIL & CORREO (Sobre geométrico 2.5D en azul zafiro con sello de notificación)
export function AuraEmailIcon({ size = 64, className = "" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
      <BaseSquircle id="mail" />
      <defs>
        <linearGradient id="mail_body" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#38bdf8" />
          <stop offset="100%" stopColor="#1d4ed8" />
        </linearGradient>
        <linearGradient id="mail_flap" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#60a5fa" />
          <stop offset="100%" stopColor="#2563eb" />
        </linearGradient>
        <linearGradient id="mail_notify" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#fb7185" />
          <stop offset="100%" stopColor="#e11d48" />
        </linearGradient>
      </defs>
      {/* Cuerpo del sobre */}
      <rect x="14" y="19" width="36" height="27" rx="4.5" fill="url(#mail_body)" />
      {/* Solapa superior en plano de luz */}
      <path d="M14 23.5C14 21 15.5 19 19 19H45C48.5 19 50 21 50 23.5L32 34L14 23.5Z" fill="url(#mail_flap)" />
      {/* Pliegues inferiores */}
      <path d="M14 43L26.5 32.5" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" opacity="0.75" />
      <path d="M50 43L37.5 32.5" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" opacity="0.75" />
      {/* Sello de correo nuevo */}
      <circle cx="46" cy="18" r="5" fill="url(#mail_notify)" stroke="#ffffff" strokeWidth="2" />
    </svg>
  );
}

// 18. BALY AI COPILOT (Núcleo de Inteligencia Artificial en degradado púrpura-rosa y chispa dorada)
export function AuraAiIcon({ size = 64, className = "" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
      <BaseSquircle id="ai" />
      <defs>
        <linearGradient id="ai_grad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#a855f7" />
          <stop offset="100%" stopColor="#ec4899" />
        </linearGradient>
      </defs>
      {/* Letras geométricas modernas 'AI' */}
      <path d="M26 43L23.2 36.5H16.8L14 43H8.5L17.5 21H22.5L31.5 43H26ZM20 25.8L18.2 31.8H21.8L20 25.8Z" fill="url(#ai_grad)" />
      <path d="M36 21H42.5V43H36V21Z" fill="url(#ai_grad)" />
      {/* Chispa de inteligencia superior */}
      <path d="M49 14L50.5 18.5L55 20L50.5 21.5L49 26L47.5 21.5L43 20L47.5 18.5L49 14Z" fill="#eab308" />
    </svg>
  );
}

// MASTER SUITE
export function AuraPymesIcon({ size = 64, className = "" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
      <BaseSquircle id="pyme" />
      <rect x="18" y="20" width="28" height="28" rx="4" fill="#0284c7" />
      <rect x="22" y="24" width="4" height="4" rx="1" fill="#bae6fd" />
      <rect x="28" y="24" width="4" height="4" rx="1" fill="#bae6fd" />
      <rect x="34" y="24" width="4" height="4" rx="1" fill="#bae6fd" />
      <rect x="22" y="32" width="4" height="4" rx="1" fill="#bae6fd" />
      <rect x="28" y="32" width="4" height="4" rx="1" fill="#bae6fd" />
      <rect x="34" y="32" width="4" height="4" rx="1" fill="#bae6fd" />
      <rect x="27" y="40" width="8" height="8" fill="#ffffff" />
    </svg>
  );
}

export function AuraSubscriptionsIcon({ size = 64, className = "" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
      <BaseSquircle id="sub" />
      <rect x="14" y="20" width="36" height="24" rx="4.5" fill="#7c3aed" />
      <rect x="14" y="25" width="36" height="5" fill="#4c1d95" />
      <rect x="19" y="34" width="6" height="4.5" rx="1" fill="#facc15" />
      <circle cx="41" cy="36" r="3" fill="#f43f5e" opacity="0.9" />
    </svg>
  );
}

export function AuraSecurityIcon({ size = 64, className = "" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
      <BaseSquircle id="sec" />
      <path d="M32 15L19 21V32C19 40.5 24.5 48 32 50C39.5 48 45 40.5 45 32V21L32 15Z" fill="#10b981" />
      <path d="M26 33L30 37L38 28" stroke="#ffffff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// 19. CIERRES Y COMISIONES (Moneda y prisma de split financiero 2.5D en esmeralda y oro)
export function AuraCommissionsIcon({ size = 64, className = "" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
      <BaseSquircle id="comm" />
      <defs>
        <linearGradient id="comm_emerald" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#10b981" />
          <stop offset="100%" stopColor="#059669" />
        </linearGradient>
        <linearGradient id="comm_gold" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#fbbf24" />
          <stop offset="100%" stopColor="#d97706" />
        </linearGradient>
        <linearGradient id="comm_violet" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#a78bfa" />
          <stop offset="100%" stopColor="#7c3aed" />
        </linearGradient>
      </defs>
      {/* Disco base de cierre/transacción */}
      <circle cx="32" cy="32" r="16" fill="url(#comm_emerald)" />
      {/* Segmento de reparto/comisión superpuesto en arco dorado */}
      <path
        d="M32 16C40.8366 16 48 23.1634 48 32C48 34.5 47.4 36.8 46.4 38.9L32 32V16Z"
        fill="url(#comm_gold)"
      />
      {/* Prisma central con símbolo de éxito monetario */}
      <circle cx="32" cy="32" r="7" fill="#ffffff" />
      <path
        d="M32 28V36M29.5 30C29.5 29 30.5 28.5 32 28.5C33.5 28.5 34.5 29 34.5 30.5C34.5 32.5 29.5 32 29.5 33.8C29.5 35 30.5 35.5 32 35.5C33.5 35.5 34.5 35 34.5 34"
        stroke="#059669"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
      {/* Estrella o destello de liquidación */}
      <circle cx="45" cy="20" r="3" fill="#fbbf24" />
    </svg>
  );
}

// 20. CONFIGURACIÓN DE EMPRESA / SEDE CORPORATIVA (Edificio corporativo con frontón, pórtico y emblema dorado en púrpura Odoo)
export function AuraAgencyIcon({ size = 64, className = "" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
      <BaseSquircle id="agency" />
      <defs>
        <linearGradient id="agency_pediment" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#9d3e84" />
          <stop offset="50%" stopColor="#714B67" />
          <stop offset="100%" stopColor="#4e2345" />
        </linearGradient>
        <linearGradient id="agency_facade" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#8b3272" />
          <stop offset="100%" stopColor="#4c163e" />
        </linearGradient>
        <linearGradient id="agency_gold" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#fde047" />
          <stop offset="100%" stopColor="#d97706" />
        </linearGradient>
        <filter id="agency_shadow" x="-10%" y="-10%" width="120%" height="120%">
          <feDropShadow dx="0" dy="1.5" stdDeviation="1.5" floodColor="#3b0f31" floodOpacity="0.25" />
        </filter>
      </defs>

      <g filter="url(#agency_shadow)">
        {/* Frontón / Remate arquitectónico superior clásico */}
        <path d="M32 14L17 22H47L32 14Z" fill="url(#agency_pediment)" />
        {/* Cornisa divisoria */}
        <rect x="16" y="22" width="32" height="2.5" rx="1.2" fill="#4d1b40" />
        
        {/* Fachada principal del edificio corporativo */}
        <rect x="18" y="24.5" width="28" height="23.5" rx="2" fill="url(#agency_facade)" />
        
        {/* Ventanales corporativos iluminados (2 columnas x 2 filas) */}
        <rect x="21.5" y="27.5" width="4.5" height="4.5" rx="1" fill="#ffffff" opacity="0.88" />
        <rect x="38" y="27.5" width="4.5" height="4.5" rx="1" fill="#ffffff" opacity="0.88" />
        <rect x="21.5" y="34.5" width="4.5" height="4.5" rx="1" fill="#ffffff" opacity="0.88" />
        <rect x="38" y="34.5" width="4.5" height="4.5" rx="1" fill="#ffffff" opacity="0.88" />
        
        {/* Pórtico / Entrada principal con arco institucional */}
        <path d="M28.5 48V36.5C28.5 34.6 30 33 32 33C34 33 35.5 34.6 35.5 36.5V48H28.5Z" fill="#ffffff" />
        <path d="M30 48V37.5C30 36.4 30.9 35.5 32 35.5C33.1 35.5 34 36.4 34 37.5V48H30Z" fill="#cbd5e1" />
        
        {/* Emblema corporativo dorado en el frontón superior */}
        <circle cx="32" cy="18.5" r="2.4" fill="url(#agency_gold)" />
        <circle cx="32" cy="18.5" r="1.1" fill="#ffffff" opacity="0.75" />

        {/* Destello sutil de estatus corporativo */}
        <circle cx="44" cy="18" r="2" fill="#fbbf24" />
      </g>
    </svg>
  );
}

export const AuraCompanyIcon = AuraAgencyIcon;

// 25. AUTOMATIZACIONES (Rayo dinámico con nodos de flujo en degradado ámbar, cian y violeta)
export function AuraAutomationsIcon({ size = 64, className = "" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
      <BaseSquircle id="aut" />
      <defs>
        <linearGradient id="aut_purple" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#8b5cf6" />
          <stop offset="100%" stopColor="#6d28d9" />
        </linearGradient>
        <linearGradient id="aut_amber" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#fbbf24" />
          <stop offset="100%" stopColor="#d97706" />
        </linearGradient>
        <linearGradient id="aut_cyan" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#06b6d4" />
          <stop offset="100%" stopColor="#0284c7" />
        </linearGradient>
        <filter id="aut_shadow" x="-10%" y="-10%" width="120%" height="120%">
          <feDropShadow dx="0" dy="1.5" stdDeviation="1.5" floodColor="#4c1d95" floodOpacity="0.25" />
        </filter>
      </defs>

      <g filter="url(#aut_shadow)">
        {/* Curva de conexión de flujo de automatización tipo canvas */}
        <path d="M19 25C26 25 24 39 31 39C38 39 36 25 44 25" stroke="#cbd5e1" strokeWidth="2.5" strokeLinecap="round" strokeDasharray="3 3" />
        
        {/* Nodos de automatización */}
        <circle cx="19" cy="25" r="4.5" fill="url(#aut_cyan)" />
        <circle cx="19" cy="25" r="1.8" fill="#ffffff" />
        
        <circle cx="45" cy="25" r="4.5" fill="url(#aut_purple)" />
        <circle cx="45" cy="25" r="1.8" fill="#ffffff" />

        {/* Rayo de energía / trigger central en degradado ámbar dorado */}
        <path d="M34 15L23 32H32L29 49L42 29H33L37 15H34Z" fill="url(#aut_amber)" />
      </g>
    </svg>
  );
}
